import { AlertTriangle, Store, Globe, Lock, CircleCheck, CircleAlert } from 'lucide-react';
import { fmtCena, fmtCislo } from './receptUtils';
import { GRID, MARZE_BARVY, stavMarze, fmtPocet } from './prehledGrid';

/**
 * Buňky sloupcové mřížky přehledu. Řádek položky i řádek kategorie kreslí
 * tytéž buňky ve stejných šířkách — jen kategorie mají souhrn (průměr, součet)
 * a tlumený vzhled, aby se daly odlišit od konkrétní položky.
 */

export const KANALY = [
    { id: 'bistro', label: 'Bistro' },
    { id: 'rozvoz', label: 'Rozvoz' },
    { id: 'vydej',  label: 'Výdej' },
];

// ── Marže po kanálech ────────────────────────────────────────────────────────
// Tři hodnoty vedle sebe ve stejném pořadí jako v hlavičce. Barvu nese jen
// dobrá marže a marže pod prahem; střední pásmo zůstává v textové barvě.
export function MarzeBunka({ marze, souhrn = false, mutedColor, isDark }) {
    const sirkaBunky = Math.floor((GRID.marze - 8) / 3);

    return (
        <div className="flex items-center" style={{ width: GRID.marze, flexShrink: 0, gap: 4 }}>
            {KANALY.map(k => {
                const d = marze?.[k.id];
                const pct = souhrn ? d : (d?.cena > 0 ? d?.pct : null);
                const stav = stavMarze(pct);
                const barva = MARZE_BARVY[stav];
                const popis = pct == null
                    ? `${k.label}: bez prodejní ceny`
                    : `${k.label}: marže ${souhrn ? Math.round(pct) : pct} %${d?.cena ? ` · cena ${fmtCena(d.cena)}` : ''}`;

                if (souhrn) {
                    return (
                        <span key={k.id} title={popis}
                            className="text-[11px] tabular-nums text-center"
                            style={{ width: sirkaBunky, color: barva || mutedColor, fontWeight: barva ? 600 : 400 }}>
                            {pct == null ? '—' : Math.round(pct)}
                        </span>
                    );
                }

                return (
                    <span key={k.id} title={popis}
                        className="text-[11px] font-semibold tabular-nums text-center inline-flex items-center justify-center"
                        style={{
                            width: sirkaBunky, height: 20, borderRadius: 5, gap: 2,
                            color: barva || 'var(--text-primary)',
                            background: barva
                                ? `${barva}${isDark ? '2e' : '1f'}`
                                : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'),
                        }}>
                        {stav === 'kriticka' && <AlertTriangle size={9} />}
                        {pct == null ? '—' : Math.round(pct)}
                    </span>
                );
            })}
        </div>
    );
}

// ── Náklad → cena ────────────────────────────────────────────────────────────
// Pod čísly dvoubarevný proužek: kolik z prodejní ceny spolkne náklad a co
// zbude jako marže. Bez ceny (polotovar) jen náklad, proužek by lhal.
export function NakladCenaBunka({ naklad, cena, jednotka, souhrn = false, mutedColor, isDark }) {
    const maCenu = (cena ?? 0) > 0 && (naklad ?? 0) > 0;
    const podilNakladu = maCenu ? Math.max(0, Math.min(1, naklad / cena)) : 0;
    const prekroceno = maCenu && naklad > cena;

    return (
        <div style={{ width: GRID.naklad, flexShrink: 0, textAlign: 'right' }}>
            <div className="text-[12px] tabular-nums" style={{ fontFamily: 'ui-monospace, monospace' }}>
                {souhrn && <span style={{ color: mutedColor }}>ø </span>}
                <span style={{ color: maCenu ? mutedColor : 'var(--text-primary)', fontWeight: maCenu ? 400 : 600 }}>
                    {naklad != null ? fmtCislo(naklad) : '—'}
                </span>
                {maCenu && (
                    <>
                        <span style={{ color: mutedColor }}> → </span>
                        <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{fmtCislo(cena)}</span>
                    </>
                )}
                {!souhrn && jednotka && <span className="text-[10px]" style={{ color: mutedColor }}>/{jednotka}</span>}
            </div>
            {maCenu && !souhrn && (
                <div className="flex" style={{ height: 3, marginTop: 4, gap: 2 }}>
                    <div style={{
                        width: `${podilNakladu * 100}%`, borderRadius: 2,
                        background: prekroceno ? MARZE_BARVY.kriticka : (isDark ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.22)'),
                    }} />
                    <div style={{ flex: 1, borderRadius: 2, background: prekroceno ? 'transparent' : MARZE_BARVY.dobra }} />
                </div>
            )}
        </div>
    );
}

// ── Prodej za 30 dní ─────────────────────────────────────────────────────────
export function Sparkline({ hodnoty = [], sirka = 62, vyska = 16, barva }) {
    if (!hodnoty.length || hodnoty.every(v => !v)) return null;
    const max = Math.max(...hodnoty, 1);
    const krok = hodnoty.length > 1 ? sirka / (hodnoty.length - 1) : sirka;
    const body = hodnoty
        .map((v, i) => `${(i * krok).toFixed(1)},${(vyska - 1 - (v / max) * (vyska - 2)).toFixed(1)}`)
        .join(' ');

    return (
        <svg width={sirka} height={vyska} viewBox={`0 0 ${sirka} ${vyska}`} aria-hidden style={{ display: 'block', flexShrink: 0 }}>
            <polyline points={body} fill="none" stroke={barva} strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
    );
}

export function ProdejBunka({ prodej, souhrn = false, mutedColor, isDark }) {
    const celkem = souhrn ? prodej : prodej?.celkem;
    const dny = souhrn ? null : prodej?.dny;

    return (
        <div className="flex items-center justify-end" style={{ width: GRID.prodej, flexShrink: 0, gap: 8 }}
            title={celkem ? `Prodáno za posledních 30 dní: ${fmtPocet(celkem)} ks` : 'Za posledních 30 dní se neprodalo'}>
            {dny && <Sparkline hodnoty={dny} barva={isDark ? '#94A3B8' : '#64748B'} />}
            <span className="text-[11px] tabular-nums" style={{ color: celkem ? 'var(--text-primary)' : mutedColor, fontWeight: souhrn ? 600 : 400 }}>
                {celkem ? `${fmtPocet(celkem)}×` : '0×'}
            </span>
        </div>
    );
}

// ── Kde se prodává + co chybí ────────────────────────────────────────────────
export function KanalyBunka({ pokladna, eshop, chybiCena, mutedColor, isDark }) {
    const chip = (Icon, text, barva) => (
        <span key={text} title={text}
            className="inline-flex items-center text-[10px] font-medium"
            style={{
                gap: 3, padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap',
                background: barva ? `${barva}${isDark ? '2e' : '1f'}` : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'),
                color: barva || mutedColor,
            }}>
            <Icon size={10} />{text}
        </span>
    );

    return (
        // Zbytek volného místa (nad strop názvu) padá sem — proto flex, ne pevná šířka.
        <div className="flex items-center flex-wrap" style={{ flex: '1 1 auto', minWidth: GRID.kanaly, gap: 4 }}>
            {pokladna && chip(Store, 'Pokladna')}
            {eshop && chip(Globe, 'E-shop')}
            {!pokladna && !eshop && chip(Lock, 'Jen v receptáři')}
            {chybiCena && chip(AlertTriangle, 'Chybí cena', MARZE_BARVY.kriticka)}
        </div>
    );
}

// ── Stav ─────────────────────────────────────────────────────────────────────
export function StavBunka({ validace, problemu = null, mutedColor }) {
    if (problemu != null) {
        return (
            <div className="text-[11px] font-semibold tabular-nums text-right" style={{ width: GRID.stav, flexShrink: 0, color: problemu ? MARZE_BARVY.kriticka : mutedColor }}>
                {problemu || '—'}
            </div>
        );
    }

    const stav = validace?.stav;
    const vPoradku = !stav || stav === 'green';
    const Icon = vPoradku ? CircleCheck : CircleAlert;
    const barva = vPoradku ? MARZE_BARVY.dobra : (stav === 'red' ? MARZE_BARVY.kriticka : mutedColor);
    const popis = vPoradku ? 'Bez nálezů' : (validace?.chyby?.[0] || validace?.varovani?.[0] || 'Vyžaduje pozornost');

    return (
        <div className="flex items-center justify-end" style={{ width: GRID.stav, flexShrink: 0 }} title={popis}>
            <Icon size={16} style={{ color: barva }} />
        </div>
    );
}
